import moment, { Moment } from "moment";
import { extractTextFromHtml } from '@home/utils/common';
import { pageReportTypeConfig } from "@intelligence/_config/reportConfig";
import { buildReportAuthors } from '@intelligence/components/ReportCard/authors';
import { highlightText } from '@/pages/dashboard/components/FilterSelect/utils';
import { IntelligenceReportDataInfo, IReportDateRanges } from "./type";
import { getDefaultDisplayTimeRange } from "./components/ParentDateFilter/_config";

export { getDefaultDisplayTimeRange };

/** 标签分隔符正则（字符串形式），默认值 [,|/\\、；;。.] */
export const DEFAULT_TAG_SEPARATOR_REG_STR = '[,，|/\\\\、；;。.]';

/**
 * 按分隔符拆分标签字符串，去重后返回
 * @param tagList 原始标签数组
 * @param tagSeparatorReg 标签分隔符正则（字符串形式）
 */
export const splitReportTags = (
  tagList: string[] | null | undefined,
  tagSeparatorReg?: string,
): string[] => {
  if (!Array.isArray(tagList)) return [];
  const separatorRegStr = tagSeparatorReg || DEFAULT_TAG_SEPARATOR_REG_STR;
  const SEPARATOR_REG = new RegExp(separatorRegStr);
  const result: string[] = [];
  tagList.forEach((item: string) => {
    if (typeof item === 'string') {
      const parts = item.split(SEPARATOR_REG).map((s) => s.trim()).filter(Boolean);
      result.push(...parts);
    } else {
      result.push(item);
    }
  });
  return [...new Set(result)];
};

/** extraToolList 已配置且包含 bulletedList 时，启用无序列表相关能力 */
export const hasBulletedListTool = (extraToolList?: string[]): boolean =>
  Array.isArray(extraToolList) && extraToolList.includes('bulletedList');

/**
 * 摘要纯文本比对：启用 bulletedList 时剥离列表/段落壳层，避免多 li 仅结构差异触发 Editor's notes
 */
export const normalizeIntroForCompare = (
  html: string | null | undefined,
  enableBulletedList: boolean,
): string => {
  const raw = html ?? '';
  if (!enableBulletedList) {
    return extractTextFromHtml(raw).trim();
  }
  const withoutListShell = raw
    .replace(/<\/?(ul|ol|li|p|div|br)\b[^>]*>/gi, ' ')
    .replace(/(<\/?[^>]*>)|(\s*&[a-zA-Z]+;\s*)/g, '');
  return withoutListShell.replace(/\s+/g, ' ').trim();
};

export const formatDateRangeToStore = (
  range: [Moment, Moment],
): { startTime: string; endTime: string } => ({
  startTime: range[0].format('YYYY-MM-DD HH:mm:ss'),
  endTime: range[1].format('YYYY-MM-DD HH:mm:ss'),
});

/**
 * 转换报告数据为 InsightsReportCard 所需格式
 * 注意：Toggle 数据切换已在 IntelligenceReports 主组件的 displayReports 中完成，
 * 此函数直接读取 report.introduction / report.tag 即可
 * @param report 原始报告数据
 * @param searchKeyword 搜索关键词（用于高亮）
 */
export const convertToInsightsReportData = (
  report: IntelligenceReportDataInfo,
  searchKeyword = '',
  tagSeparatorReg?: string,
) => {
  const keyword = searchKeyword?.trim();
  const tagEn = splitReportTags(report.tag_en, tagSeparatorReg);
  const tagCn = splitReportTags(report.tag, tagSeparatorReg);

  return {
    id: report.id,
    resource_id: report.resource_id,
    // 标题：应用搜索高亮
    title: keyword 
      ? [highlightText(report.title_en, keyword), highlightText(report.title, keyword)]
      : [report.title_en, report.title],
    // 摘要：保持原样
    summary: [report.introduction_en, report.introduction],
    // 标签：拆分后应用搜索高亮
    tag: keyword
      ? [
        tagEn.map(tag => highlightText(tag, keyword)),
        tagCn.map(tag => highlightText(tag, keyword)),
      ]
      : [tagEn, tagCn],
    coverImg: report.cover,
    creator_department: [report.creator_department_en, report.creator_department],
    creator_head_img: report.creator_head_picture,
    creator_name: [report.creator_name_en, report.creator_name],
    authors: buildReportAuthors(report),
    email: report.email,
    time: moment(report.create_time).format('YYYY-MM-DD'),
    report_type: [report.report_type_en, report.report_type],
    watched_cnt: report.watched_cnt,
    report_like: report.report_like,
    pdf: report.pdf,
    pdf_en: report.pdf_en,
  };
};

/**
 * 筛选和排序要展示的报告列表
 * 1. 必须包含 studio_start_time、studio_end_time、studio_sort 字段
 * 2. studio_start_time 不能在今天之后
 * 3. studio_end_time 不能在今天之前
 * 4. 支持按 segment 过滤
 * 5. 按 studio_sort 升序排序
 */
export const filterAndSortReports = (
  data: IntelligenceReportDataInfo[],
  timeZone: number | string,
  segmentValue?: string,
  segmentField = 'system'
): IntelligenceReportDataInfo[] => {
  if (!Array.isArray(data) || data.length === 0) return [];
  
  // 获取今天的开始时间（考虑时区）
  const todayStart = moment().utcOffset(Number(timeZone) || 0).startOf('day');
  
  // 筛选符合条件的报告
  let filtered = data.filter((report) => {
    // 必须有这三个字段
    if (!report.studio_start_time || !report.studio_end_time || report.studio_sort === undefined) {
      return false;
    }
    
    try {
      // 解析开始和结束时间（应用时区）
      const startTime = moment(report.studio_start_time).utcOffset(Number(timeZone) || 0);
      const endTime = moment(report.studio_end_time).utcOffset(Number(timeZone) || 0);
      
      // studio_start_time 不能在今天之后
      if (startTime.isAfter(todayStart)) {
        return false;
      }
      
      // studio_end_time 不能在今天之前（即必须是今天或今天之后）
      if (endTime.isBefore(todayStart)) {
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('解析报告日期失败:', error);
      return false;
    }
  });
  
  // 根据 segment 值过滤
  if (segmentValue && segmentValue !== 'all') {
    filtered = filtered.filter(report => report[segmentField as keyof IntelligenceReportDataInfo] === segmentValue);
  }
  
  // 按 studio_sort 排序（升序）
  return filtered.sort((a, b) => {
    const sortA = a.studio_sort ?? Number.MAX_SAFE_INTEGER;
    const sortB = b.studio_sort ?? Number.MAX_SAFE_INTEGER;
    return sortA - sortB;
  });
};

/**
 * 根据当前 tab 和搜索关键字过滤报告列表（用于编辑弹窗）
 */
export const filterReportsByTabAndKeyword = (
  data: IntelligenceReportDataInfo[],
  activeTabId: string,
  searchKeyword: string,
  isEn: boolean,
  tabField = 'system'
): IntelligenceReportDataInfo[] => {
  if (!Array.isArray(data)) return [];
  
  let filtered = data;
  
  // 根据tab筛选 - 按指定字段过滤
  if (activeTabId && activeTabId !== 'all') {
    filtered = filtered.filter((item) => item[tabField as keyof IntelligenceReportDataInfo] === activeTabId);
  }
  
  // 根据搜索关键字筛选
  if (searchKeyword?.trim()) {
    const keyword = searchKeyword.trim().toLowerCase();
    filtered = filtered.filter((item) => {
      const title = (isEn ? item.title_en : item.title)?.toLowerCase() || '';
      const tags = isEn ? item.tag_en : item.tag;
      const tagMatch = tags?.some(tag => tag?.toLowerCase().includes(keyword)) ?? false;
      
      return title.includes(keyword) || tagMatch;
    });
  }
  
  // 左侧列表不排序，保持原始数据顺序
  return filtered;
};

/**
 * 判断报告是否应该默认勾选
 * 规则：studio_end_time 不在今天之前（即今天或今天之后）
 */
export const shouldDefaultSelect = (report: IntelligenceReportDataInfo): boolean => {
  if (!report?.studio_end_time) {
    return false;
  }
  
  try {
    // 获取当前时间（考虑时区），使用moment处理
    const todayStart = moment().startOf('day');
    
    // 解析 studio_end_time，应用时区
    const endTime = moment(report.studio_end_time);
    
    // 如果 studio_end_time 不是在今天之前（即今天或今天之后），则勾选
    return endTime.isSameOrAfter(todayStart);
  } catch (error) {
    console.error('解析日期失败:', error);
    return false;
  }
};

/** 按发布时间 create_time 倒序排序 */
export const sortReportsByCreateTimeDesc = (
  reports: IntelligenceReportDataInfo[],
): IntelligenceReportDataInfo[] => {
  return [...reports].sort((a, b) => {
    const timeA = moment(a.create_time).valueOf();
    const timeB = moment(b.create_time).valueOf();
    const validA = Number.isFinite(timeA);
    const validB = Number.isFinite(timeB);
    if (!validA && !validB) return 0;
    if (!validA) return 1;
    if (!validB) return -1;
    return timeB - timeA;
  });
};

/**
 * 初始化默认勾选的报告和日期范围
 * 返回默认选中的报告列表和日期范围对象
 */
export const initializeDefaultSelectedReports = (
  data: IntelligenceReportDataInfo[],
): { selectedReports: IntelligenceReportDataInfo[]; dateRanges: IReportDateRanges; } => {
  if (!Array.isArray(data) || data.length === 0) {
    return { selectedReports: [], dateRanges: {} };
  }

  // 打开弹窗时按已保存的 studio_sort 还原顺序
  const defaultSelected = data
    .filter(report => shouldDefaultSelect(report))
    .sort((a, b) => {
      const sortA = a.studio_sort ?? Number.MAX_SAFE_INTEGER;
      const sortB = b.studio_sort ?? Number.MAX_SAFE_INTEGER;
      return sortA - sortB;
    });
  
  // 同时初始化日期范围
  const initialDateRanges: IReportDateRanges = {};
  defaultSelected.forEach(report => {
    if (report.studio_start_time && report.studio_end_time) {
      initialDateRanges[report.resource_id] = {
        startTime: report.studio_start_time,
        endTime: report.studio_end_time
      };
    }
  });
  
  return {
    selectedReports: defaultSelected,
    dateRanges: initialDateRanges
  };
};


/**
 * 根据 system 获取对应的 reportTypeConfig
 * @param system 报告的 system 字段
 * @returns 对应的 reportTypeConfig，如果找不到则返回自定义的 studioReport 配置
 */
export const getReportTypeConfig = (system: string) => {
  // 根据 system 映射到对应的 pageReportTypeConfig
  const configMap: { [key: string]: typeof pageReportTypeConfig.studioReport; } = {
    intelligence_insights: pageReportTypeConfig.insightsReports,
    intelligence_iic: pageReportTypeConfig.insightsCenterLibrary,
    gameResearch: pageReportTypeConfig.gameResearchReport,
    intelligence_ai: pageReportTypeConfig.aiHubReports,
    intelligence: {
      ...pageReportTypeConfig.insightsReports,
      systemIdCur: 'intelligence', // Market Reports 使用 intelligence 作为 systemId
      downloadLogKeyCur: 'MarketReportsDownload',
    },
  };

  return configMap[system] || {
    ...pageReportTypeConfig.studioReport,
    showLiked: true,  // 展示点赞
    showView: false,  // 不展示浏览量
    likedClickable: true, // 点赞可点击
  };
};